import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Box, Grid, LinearProgress } from '@mui/material';

import { LayoutDashboard } from '../../../shared/layouts';
import { GenericService } from '../../../shared/services';
import { AccessRoleDTO, AccessRoleFilterDTO, ApplicationDTO, ApplicationFilterDTO, FeatureDTO, FeatureFilterDTO } from '../../../shared/interfaces';
import { ISnackbarAlert, SnackbarAlert, CardList, TextFieldInput, ToolBar, IDialogActions, ICardActions, ICardColumns, IChipFilter, SelectField } from '../../../shared/components';
import { FeatureMaintenance } from './FeatureMaintenance';
import { Delete } from '../../../shared/components/confirm-delete/ConfirmDelete';
import { ListId, LocalStorange } from '../../../shared/environment';
import { Attribute } from '../attribute/Attribute';

interface IFeatureProps {
    data: any;
}

export const Feature: React.FC<IFeatureProps> = ({ data }) => {

    const [applicationList, setApplicationList] = useState<ApplicationDTO[]>([]);
    const [featureList, setFeatureList] = useState<FeatureDTO[]>([]);
    const [featureFilterDTO, setFeatureFilterDTO] = useState<FeatureFilterDTO>();

    const [actionExecuted, setActionExecuted] = useState(false);
    const [newRecord, setNewRecord] = useState('');
    const [editRecord, setEditRecord] = useState('');
    const [deleteRecord, setDeleteRecord] = useState('');
    const [expadedData, setExpandedData] = useState('');
    const [isOpenMaintenance, setIsOpenMaintenance] = useState(false);

    const [loading, setLoading] = useState(false);
    const [snackBarMessage, setSnackBarMessage] = useState<ISnackbarAlert>();
    const [isOpenSnackBar, setIsOpenSnackBar] = useState(false);

    const [accessRolePermissionDTO, setAccessRolePermissionDTO] = useState<AccessRoleDTO>();
    const userInfo = JSON.parse(localStorage.getItem(LocalStorange.USERINFO) || '');

    useEffect(() => {
        setFeatureFilterDTO({ ...featureFilterDTO, name: '', applicationId: data.id ?? '' });
        handleApplicationGetAllAsync();

        handleGetPermission(ListId.appsphere.featureId, setAccessRolePermissionDTO);
    }, []);

    useEffect(() => {
        handleFeatureGetAllViewAsync();
    }, [featureFilterDTO]);

    useEffect(() => {
        if (actionExecuted)
            handleFeatureGetAllViewAsync();
    }, [actionExecuted]);

    const handleGetPermission = useCallback(async (featureId: string, setList: any) => {
        const accessRoleFilterDTO: AccessRoleFilterDTO = {
            featureId: featureId,
            userId: userInfo.user.id,
        };
        GenericService.PostGenericAsync1('authsphere.AccessRole', 'GetPermissionAsync',accessRoleFilterDTO, setActionExecuted, setLoading, undefined,  setList);
    }, []);
    
    const handleApplicationGetAllAsync = useCallback(async () => {
        const applicationFilterDTO: ApplicationFilterDTO = {
            name: '',
            typeId: '',
            companyId: userInfo.user.companyId,
        };
        GenericService.GetAllAsync1('appsphere.application', applicationFilterDTO, setLoading, setApplicationList, showMessage);
    }, []);

    const handleFeatureGetAllViewAsync = useCallback(async () => {
        if (featureFilterDTO === undefined)
            return;
        if(accessRolePermissionDTO?.allowGetAllView == undefined)
            return; 
        if(!accessRolePermissionDTO?.allowGetAllView) {
            showMessage('There is no permission for get all view.', 'info');
            return;
        }
        GenericService.GetAllViewAsync1('appsphere.feature', featureFilterDTO, setActionExecuted, setLoading, setFeatureList, showMessage);
    }, [featureFilterDTO, accessRolePermissionDTO]);

    const handleClearFilterClick = () => {
        setFeatureFilterDTO({ ...featureFilterDTO, name: '' } as FeatureFilterDTO);
    };

    function handleSearchClick() {
        handleFeatureGetAllViewAsync();
    }

    const handleNew = () => {
        if(!accessRolePermissionDTO?.allowAdd) {
            showMessage('There is no permission for new record.', 'info');
            return;
        }
        setNewRecord('data');
        setEditRecord('');
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleViewDetatils = (data: string) => {
        if(!accessRolePermissionDTO?.allowGetById) {
            showMessage('There is no permission for view details.', 'info');
            return;
        }
        setNewRecord('');
        setEditRecord(data);
        setDeleteRecord('');
        setIsOpenMaintenance(true);
    };

    const handleDelete = (data: string) => {
        if(!accessRolePermissionDTO?.allowDelete) {
            showMessage('There is no permission for delete record.', 'info');
            return;
        }
        setNewRecord('');
        setDeleteRecord(data);
        setEditRecord('');
        setIsOpenMaintenance(true);
    };

    const toolBarActions: IDialogActions[] = [
        { id: 'new', text: 'New Feature', icon: 'add_circle_outlined', iconColor: 'primary', setAction: handleNew },
    ];

    const cardActions: ICardActions[] = [
        { id: 'viewDetails', toolTip: 'View Details', icon: 'preview_outlined', iconColor: 'primary', setAction: handleViewDetatils },
        { id: 'delete', toolTip: 'Delete', icon: 'delete_outlined', iconColor: 'error', setAction: handleDelete },
    ];

    const chipFilters: IChipFilter[] = [
        { label: 'Total Records', value: featureList.length },
        { label: 'Name', value: featureFilterDTO?.name },
        { label: 'Application', value: !data ? applicationList.find(x => x.id === featureFilterDTO?.applicationId)?.name : undefined },
    ];

    const columns: ICardColumns[] = [
        { field: 'name', label: 'Name', size: 3, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
        { field: 'pathUrl', label: 'Path Url', size: 9, type: 'text', visible: true, isChip: false, hasFieldLine: false, hasToolTip: true },
    ];

    function showMessage(message: string, type: 'error' | 'info' | 'success' | 'warning') {
        const msg: ISnackbarAlert = {
            id: 'message',
            message: message,
            type: type,
        };
        setSnackBarMessage(msg);
        setIsOpenSnackBar(true);
    }
    return (
        <React.Fragment>
            {isOpenSnackBar && (
                <SnackbarAlert
                    snackBarAlert={snackBarMessage}
                    setIsOpen={setIsOpenSnackBar}
                    isOpen={isOpenSnackBar}
                />
            )}
            <LayoutDashboard title='APPLICATION'>
                <ToolBar
                    clickSearch={handleSearchClick}
                    clickClearFilter={handleClearFilterClick}
                    chipFilters={chipFilters}
                    actions={toolBarActions}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} md={4}>
                            <TextFieldInput
                                id='name'
                                label='Name'
                                setValue={setFeatureFilterDTO}
                                value={featureFilterDTO?.name}
                                obj={featureFilterDTO}
                                placeholder={'Name'}
                                maxLength={200}
                            />
                        </Grid>
                        {!data && (
                            <Grid item xs={12} md={4}>
                                <SelectField
                                    id='applicationId'
                                    label='Application Name'
                                    setValue={setFeatureFilterDTO}
                                    value={featureFilterDTO?.applicationId}
                                    obj={featureFilterDTO}
                                    valueList={applicationList}
                                />
                            </Grid>
                        )}
                    </Grid>
                </ToolBar>
                {loading && (
                    <LinearProgress sx={{ borderRadius: 3, mt: - 0.7, mb: 0.2 }} />
                )}
                <CardList
                    columns={columns}
                    rows={featureList}
                    actions={cardActions}
                    hasChildren={true}
                    setRowExpanded={setExpandedData}
                    cardSize={12}
                    childrenTitle={'ATTTRIBUTE'}
                    childrenComponent={(
                        <Box width={'100%'}>
                            <Attribute data={expadedData} />
                        </Box>
                    )}
                />
                {newRecord && (
                    <FeatureMaintenance
                        data={''}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                        parentId={featureFilterDTO?.applicationId}
                    />
                )}
                {editRecord && (
                    <FeatureMaintenance
                        data={editRecord}
                        setIsOpenMaintenance={setIsOpenMaintenance}
                        isOpenMaintenance={isOpenMaintenance}
                        setActionExecuted={setActionExecuted}
                    />
                )}
                {deleteRecord && (<Delete
                    entity={'appsphere.feature'}
                    data={deleteRecord}
                    setIsOpenDiaglog={setIsOpenMaintenance}
                    isOpenDialog={isOpenMaintenance}
                    setActionExecuted={setActionExecuted}
                />)}
            </LayoutDashboard>
        </React.Fragment >
    );
};